import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Printer, ShieldCheck } from 'lucide-react';
import { InvoiceStatus } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDate, formatDateTime } from '@/utils/format';
import { fetchInvoiceVerification, useInvoiceQuery } from '../api/sales.api';
import { InvoiceStatusBadge, OrderPaymentBadge } from '../components/InvoiceBadges';
import { printInvoice } from '../invoicePrint';

/**
 * An issued invoice, exactly as it was raised.
 *
 * Every figure and line is the frozen copy stored at issue, never a live read of the catalogue, so a reprint
 * years from now shows what the customer was actually charged. The money the order still owes is the
 * order's, from the canonical receivable, and is labelled as the order's. The page reads, prints and links;
 * it never voids, re-issues or edits anything.
 */
export function InvoicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invoiceId = '' } = useParams<{ invoiceId: string }>();
  const { format } = useCurrency();
  const { locale } = useLocale();
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isPending, isError, error } = useInvoiceQuery(invoiceId);

  if (isPending) return <LoadingState />;
  if (isError || !data) {
    return <Alert tone="error">{apiErrorMessage(error) ?? t('sales.errors.loadInvoice')}</Alert>;
  }

  /** The window opens on the click itself, so no popup blocker stops it; it learns its address once the server gives it. */
  const openVerification = () => {
    setActionError(null);
    const opened = window.open('', '_blank');
    if (opened) opened.opener = null;
    fetchInvoiceVerification(data.id)
      .then(({ url }) => {
        if (opened) opened.location.href = url;
        else window.open(url, '_blank', 'noopener');
      })
      .catch((caught: unknown) => {
        opened?.close();
        setActionError(apiErrorMessage(caught) ?? t('sales.invoice.verificationError'));
      });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.reference}
        subtitle={t('sales.invoice.subtitle', { order: data.orderReference })}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => void navigate('/sales/invoices')}>
              <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden />
              {t('sales.invoice.backToInvoices')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void navigate(`/sales/orders/${data.orderId}`)}
            >
              <ExternalLink className="size-4" aria-hidden />
              {t('sales.invoice.openOrder')}
            </Button>
            <Button variant="secondary" onClick={openVerification}>
              <ShieldCheck className="size-4" aria-hidden />
              {t('sales.invoice.openVerification')}
            </Button>
            <Button onClick={() => printInvoice(data.id)}>
              <Printer className="size-4" aria-hidden />
              {t('sales.invoice.print')}
            </Button>
          </div>
        }
      />

      {actionError ? <Alert tone="error">{actionError}</Alert> : null}
      {data.status === InvoiceStatus.VOID ? (
        <Alert tone="error">{t('sales.invoice.voidNotice')}</Alert>
      ) : null}
      {data.notice ? <Alert tone="info">{t(`sales.verify.notice.${data.notice}`)}</Alert> : null}

      <Card>
        <dl className="border-line grid gap-4 border-b p-5 sm:grid-cols-2 lg:grid-cols-5">
          <Field label={t('sales.invoices.fields.status')}>
            <InvoiceStatusBadge status={data.status} />
          </Field>
          <Field label={t('sales.invoices.fields.order')}>
            <span className="font-mono" dir="ltr">
              {data.orderReference}
            </span>
          </Field>
          <Field label={t('sales.invoices.fields.issuedOn')}>
            <span title={formatDateTime(data.issuedAt, locale)}>
              {formatDate(data.issuedAt, locale)}
            </span>
          </Field>
          <Field label={t('sales.invoices.fields.branch')}>
            <span dir="auto">{data.branchName}</span>
          </Field>
          <Field label={t('sales.invoice.customer')}>
            <span dir="auto">{data.customerNameSnapshot ?? t('sales.invoice.walkIn')}</span>
          </Field>
        </dl>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <caption className="sr-only">{t('sales.invoice.linesCaption')}</caption>
            <thead>
              <tr className="bg-surface-2 text-muted">
                <th className="px-4 py-2 text-start font-medium">
                  {t('sales.fields.description')}
                </th>
                <th className="px-4 py-2 text-end font-medium">{t('sales.fields.quantity')}</th>
                <th className="px-4 py-2 text-end font-medium">{t('sales.fields.unitPrice')}</th>
                <th className="px-4 py-2 text-end font-medium">{t('sales.invoice.discount')}</th>
                <th className="px-4 py-2 text-end font-medium">{t('sales.fields.taxShort')}</th>
                <th className="px-4 py-2 text-end font-medium">{t('sales.fields.lineTotal')}</th>
              </tr>
            </thead>
            <tbody className="divide-line divide-y">
              {data.lines.map((line) => (
                <tr key={line.id}>
                  <td className="px-4 py-2">
                    <span className="text-ink block">{line.description}</span>
                    {line.sku ? (
                      <span className="text-muted font-mono text-xs" dir="ltr">
                        {line.sku}
                        {line.taxTreatment !== 'STANDARD'
                          ? ` · ${t(`sales.taxTreatments.${line.taxTreatment}`)}`
                          : ''}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-end tabular-nums" dir="ltr">
                    {line.quantity}
                  </td>
                  <td className="px-4 py-2 text-end tabular-nums" dir="ltr">
                    {format(line.unitPriceMinor)}
                  </td>
                  <td className="px-4 py-2 text-end tabular-nums" dir="ltr">
                    {line.discountMinor > 0 ? `− ${format(line.discountMinor)}` : '—'}
                  </td>
                  <td className="px-4 py-2 text-end tabular-nums" dir="ltr">
                    {format(line.taxMinor)}
                  </td>
                  <td className="text-ink px-4 py-2 text-end font-medium tabular-nums" dir="ltr">
                    {format(line.totalMinor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <dl className="border-line ms-auto max-w-sm space-y-2 border-t p-5">
          <Figure label={t('sales.fields.subtotal')} value={format(data.subtotalMinor)} />
          <Figure
            label={t('sales.fields.discount')}
            value={data.discountMinor > 0 ? `− ${format(data.discountMinor)}` : format(0)}
          />
          <Figure label={t('sales.fields.taxShort')} value={format(data.taxMinor)} />
          <div className="border-line border-t pt-2">
            <Figure label={t('sales.fields.total')} value={format(data.totalMinor)} strong />
          </div>
        </dl>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={t('sales.invoice.orderTitle')}
            subtitle={t('sales.invoice.orderHint')}
          />
          <div className="space-y-3 p-5 pt-0">
            <OrderPaymentBadge
              status={data.orderPaymentStatus}
              orderCancelled={data.orderCancelled}
            />
            {data.orderPayment ? (
              <dl className="space-y-2">
                <Figure
                  label={t('sales.invoice.netCollected')}
                  value={format(data.orderPayment.netCollectedMinor)}
                />
                <Figure
                  label={t('sales.invoice.outstanding')}
                  value={format(data.orderPayment.outstandingMinor)}
                  strong
                />
              </dl>
            ) : (
              <p className="text-muted text-sm">{t('sales.invoice.cancelledHint')}</p>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title={t('sales.invoice.adjustments')}
            subtitle={t('sales.invoice.adjustmentsHint')}
          />
          {data.adjustments.length === 0 ? (
            <p className="text-muted p-5 pt-0 text-sm">{t('sales.invoice.noAdjustments')}</p>
          ) : (
            <ul className="divide-line divide-y" aria-label={t('sales.invoice.adjustments')}>
              {data.adjustments.map((note) => (
                <li
                  key={note.id}
                  className="flex flex-wrap items-start justify-between gap-3 px-5 py-3"
                >
                  <div className="min-w-0 space-y-1">
                    <span className="text-ink block font-mono text-sm font-medium" dir="ltr">
                      {note.reference}
                    </span>
                    <span className="text-muted block text-xs">
                      {t(`sales.invoice.adjustmentKinds.${note.kind}`)} ·{' '}
                      {formatDate(note.issuedAt, locale)}
                    </span>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={
                        note.status === 'VOID'
                          ? 'text-muted text-sm line-through'
                          : 'text-ink text-sm font-medium tabular-nums'
                      }
                      dir="ltr"
                    >
                      {format(note.totalMinor)}
                    </span>
                    <Badge tone={note.status === 'VOID' ? 'danger' : 'neutral'}>
                      {t(`sales.invoice.adjustmentStatus.${note.status}`)}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm font-medium">{children}</dd>
    </div>
  );
}

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={strong ? 'text-ink font-medium' : 'text-muted text-sm'}>{label}</dt>
      <dd className={strong ? 'text-ink text-lg font-semibold' : 'text-ink text-sm'} dir="ltr">
        {value}
      </dd>
    </div>
  );
}
